import React, { useEffect, useRef, useState } from 'react';
import { api, BACKEND } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Trash2, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminMedia() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const load = () => { setLoading(true); api.get('/admin/media').then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
    useEffect(load, []);

    const onUpload = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        for (const file of Array.from(files)) {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('alt', file.name.split('.')[0] || '');
            try {
                await api.post('/admin/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } catch (e) {
                toast.error(`${file.name}: ${e?.response?.data?.detail || 'upload failed'}`);
            }
        }
        toast.success('Upload završen');
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
        load();
    };

    const onDelete = async (id) => {
        if (!confirm('Obrisati datoteku?')) return;
        try { await api.delete(`/admin/media/${id}`); toast.success('Obrisano'); load(); } catch { toast.error('Brisanje nije uspjelo'); }
    };

    const copyUrl = (url) => {
        // Always copy the relative path (e.g. "/api/uploads/abc.jpg") so it
        // never gets pinned to a specific hosting domain. Frontend / backend
        // always resolve it relative to the current origin.
        const relative = url.startsWith('http') ? url.replace(/^https?:\/\/[^/]+/, '') : url;
        navigator.clipboard.writeText(relative);
        toast.success('URL kopiran (relativan)');
    };

    // Render uses absolute only for the admin preview (avoids CORS issues
    // if BACKEND differs from origin during local dev). Public site uses
    // relative paths directly in <img src>.
    const fullUrl = (url) => url.startsWith('http') ? url : `${BACKEND}${url}`;

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-medium">Mediji</h1>
                    <p className="text-sm text-white/60 mt-1">Galerija svih uploadanih slika.</p>
                </div>
                <div>
                    <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} data-testid="media-upload-input" />
                    <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white" data-testid="media-upload-button">
                        {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Upload…</> : <><Upload className="mr-2 h-4 w-4" />Uploadaj slike</>}
                    </Button>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {loading ? <p className="text-white/60">Učitavanje…</p> :
                  items.length === 0 ? <p className="text-white/60 col-span-full text-center py-12">Još nema slika. Uploadajte prvu!</p> :
                  items.map((m) => (
                    <Card key={m.id} className="bg-[#111318] border-[#232A36] overflow-hidden group relative">
                        <div className="aspect-square overflow-hidden bg-[#0B0C0E]">
                            <img src={fullUrl(m.url)} alt={m.alt || ''} className="h-full w-full object-cover" />
                        </div>
                        <div className="p-3">
                            <p className="text-xs text-white/70 font-mono truncate" title={m.original_name}>{m.original_name}</p>
                            <p className="text-[10px] text-white/50 mt-1">{Math.round(m.size / 1024)} KB</p>
                            <div className="mt-3 flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => copyUrl(m.url)} className="flex-1 border-[#232A36] text-white hover:bg-[#232A36] h-8 text-xs"><Copy className="h-3 w-3 mr-1" />Copy URL</Button>
                                <Button size="icon" variant="outline" onClick={() => onDelete(m.id)} className="border-red-900 text-red-400 hover:bg-red-900/20 h-8 w-8"><Trash2 className="h-3 w-3" /></Button>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}
